import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import { siteFeatureId, type SiteFeatureId } from './SiteFeature.js'
import {
  siteFeatureGrantSourceId,
  type SiteFeatureGrantSourceId,
} from './SiteFeatureGrantSource.js'

/** Identifier type for public.site_feature_grant_ */
export type SiteFeatureGrantId = string & { __brand: 'SiteFeatureGrantId' }

/** Represents the table public.site_feature_grant_ */
export default interface SiteFeatureGrantTable {
  /** Database type: pg_catalog.int8 */
  id: ColumnType<
    SiteFeatureGrantId,
    SiteFeatureGrantId | undefined,
    SiteFeatureGrantId
  >

  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.text */
  feature: ColumnType<SiteFeatureId, SiteFeatureId, SiteFeatureId>

  /** Database type: pg_catalog.text */
  source: ColumnType<
    SiteFeatureGrantSourceId,
    SiteFeatureGrantSourceId,
    SiteFeatureGrantSourceId
  >

  /** Database type: pg_catalog.text */
  sourceRef: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.timestamptz */
  grantedAt: ColumnType<Date, Date | string | undefined, Date | string>

  /** Database type: pg_catalog.text */
  grantedBy: ColumnType<string, string, string>

  /** Database type: pg_catalog.timestamptz */
  revokedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.text */
  revokedReason: ColumnType<string | null, string | null, string | null>
}

export type SiteFeatureGrant = Selectable<SiteFeatureGrantTable>

export type SiteFeatureGrantInitializer = Insertable<SiteFeatureGrantTable>

export type SiteFeatureGrantMutator = Updateable<SiteFeatureGrantTable>

export const siteFeatureGrantId =
  z.string() as unknown as z.Schema<SiteFeatureGrantId>

export const siteFeatureGrant = z.object({
  id: siteFeatureGrantId,
  siteId: legacySiteId,
  feature: siteFeatureId,
  source: siteFeatureGrantSourceId,
  sourceRef: z.string().nullable().optional(),
  grantedAt: z.coerce.date(),
  grantedBy: z.string(),
  revokedAt: z.coerce.date().nullable().optional(),
  revokedReason: z.string().nullable().optional(),
}) as unknown as z.Schema<SiteFeatureGrant>

export const siteFeatureGrantInitializer = z.object({
  id: siteFeatureGrantId.optional(),
  siteId: legacySiteId,
  feature: siteFeatureId,
  source: siteFeatureGrantSourceId,
  sourceRef: z.string().optional().nullable(),
  grantedAt: z.coerce.date().optional(),
  grantedBy: z.string(),
  revokedAt: z.coerce.date().optional().nullable(),
  revokedReason: z.string().optional().nullable(),
}) as unknown as z.Schema<SiteFeatureGrantInitializer>

export const siteFeatureGrantMutator = z.object({
  id: siteFeatureGrantId.optional(),
  siteId: legacySiteId.optional(),
  feature: siteFeatureId.optional(),
  source: siteFeatureGrantSourceId.optional(),
  sourceRef: z.string().optional().nullable(),
  grantedAt: z.coerce.date().optional(),
  grantedBy: z.string().optional(),
  revokedAt: z.coerce.date().optional().nullable(),
  revokedReason: z.string().optional().nullable(),
}) as unknown as z.Schema<SiteFeatureGrantMutator>
